import {
  type InfiniteData,
  type QueryClient,
  infiniteQueryOptions,
  queryOptions,
} from "@tanstack/react-query";
import {
  getOperatorsHttpOrigin,
  type OperatorsSessionSearchResponse,
  type OperatorsSession,
  type OperatorsSessionsResponse,
  type OperatorsUsageMonthlyResponse,
} from "../operators";
import { readOperatorsAuthErrorMessage, throwOperatorsAuthExpired } from "../operatorsAuthSession";

export class OperatorsForbiddenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "OperatorsForbiddenError";
  }
}

export function isOperatorsForbiddenError(error: unknown): error is OperatorsForbiddenError {
  return error instanceof OperatorsForbiddenError;
}

export const OPERATORS_ARCHIVED_SESSIONS_PAGE_SIZE = 50;
export const OPERATORS_ACTIVE_SESSIONS_PAGE_SIZE = 100;

const OPERATORS_SEARCH_MIN_SERVER_QUERY_LENGTH = 3;
const OPERATORS_SEARCH_SHORT_SERVER_TOKENS = new Set(["ai", "ci", "db", "pr", "qa", "ui", "ux"]);
const OPERATORS_SEARCH_TOKEN_PATTERN = /[a-z0-9]+/gi;

export const operatorsQueryKeys = {
  all: ["operators"] as const,
  sessions: () => ["operators", "sessions"] as const,
  activeSessions: () => ["operators", "sessions", "active"] as const,
  sidebarSessions: (email: string, group: "participating" | "other") =>
    ["operators", "sessions", "sidebar", email, group] as const,
  session: (sessionId: string) => ["operators", "sessions", "detail", sessionId] as const,
  sessionSearches: () => ["operators", "sessions", "search"] as const,
  sessionSearch: (query: string, status: "active" | "archived" | "all", limit: number) =>
    ["operators", "sessions", "search", status, limit, query] as const,
  archivedSessions: (pageSize: number = OPERATORS_ARCHIVED_SESSIONS_PAGE_SIZE) =>
    ["operators", "sessions", "archived", pageSize] as const,
  usage: () => ["operators", "usage"] as const,
  usageMonthly: (month: string | null) =>
    [...operatorsQueryKeys.usage(), "monthly", month] as const,
};

export function shouldUseOperatorsSessionSearchServerQuery(query: string): boolean {
  const trimmedQuery = query.trim();
  if (trimmedQuery.length >= OPERATORS_SEARCH_MIN_SERVER_QUERY_LENGTH) {
    return true;
  }

  const tokens = trimmedQuery.toLowerCase().match(OPERATORS_SEARCH_TOKEN_PATTERN) ?? [];
  return tokens.some((token) => OPERATORS_SEARCH_SHORT_SERVER_TOKENS.has(token));
}

export function parseOperatorsSessionsResponse(value: unknown): OperatorsSessionsResponse {
  const parsed = value as Partial<OperatorsSessionsResponse> | null | undefined;
  const nextCursor =
    typeof parsed?.nextCursor === "string" && parsed.nextCursor.length > 0
      ? parsed.nextCursor
      : null;
  return {
    items: Array.isArray(parsed?.items) ? (parsed.items as OperatorsSession[]) : [],
    nextCursor,
  };
}

export function parseOperatorsSessionSearchResponse(
  value: unknown,
): OperatorsSessionSearchResponse {
  const parsed = value as Partial<OperatorsSessionSearchResponse> | null | undefined;
  return {
    items: Array.isArray(parsed?.items) ? parsed.items : [],
  };
}

function throwIfUnauthorized(response: Response): void {
  if (response.status === 401) {
    throwOperatorsAuthExpired();
  }
}

async function throwIfForbidden(response: Response, fallbackMessage: string): Promise<void> {
  if (response.status !== 403) {
    return;
  }

  const message = await readOperatorsAuthErrorMessage(response).catch(() => fallbackMessage);
  throw new OperatorsForbiddenError(message || fallbackMessage);
}

async function fetchOperatorsSessions(input: {
  group?: "participating" | "other";
  cursor?: string;
  limit?: number;
  status: "active" | "archived" | "all";
}): Promise<OperatorsSessionsResponse> {
  const url = new URL(`${getOperatorsHttpOrigin()}/api/v1/sessions`);
  url.searchParams.set("status", input.status);
  if (input.group) url.searchParams.set("group", input.group);
  if (input.cursor) {
    url.searchParams.set("cursor", input.cursor);
  }
  if (typeof input.limit === "number") {
    url.searchParams.set("limit", String(input.limit));
  }

  const response = await fetch(url, {
    credentials: "include",
  });
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have permission to load Operators sessions.");
  if (!response.ok) {
    throw new Error("Could not load Operators sessions.");
  }

  return parseOperatorsSessionsResponse(await response.json());
}

async function fetchOperatorsSession(sessionId: string): Promise<OperatorsSession> {
  const response = await fetch(`${getOperatorsHttpOrigin()}/api/v1/sessions/${sessionId}`, {
    credentials: "include",
  });
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have permission to load this Operators session.");
  if (!response.ok) {
    throw new Error("Could not load Operators session.");
  }

  return (await response.json()) as OperatorsSession;
}

async function fetchOperatorsSessionSearch(input: {
  limit?: number;
  query: string;
  signal?: AbortSignal;
  status?: "active" | "archived" | "all";
}): Promise<OperatorsSessionSearchResponse> {
  const url = new URL(`${getOperatorsHttpOrigin()}/api/v1/sessions/search`);

  const response = await fetch(url, {
    body: JSON.stringify({
      limit: input.limit,
      query: input.query,
      status: input.status ?? "all",
    }),
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
    },
    method: "POST",
    ...(input.signal ? { signal: input.signal } : {}),
  });
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have permission to search Operators sessions.");
  if (!response.ok) {
    throw new Error("Could not search Operators sessions.");
  }

  return parseOperatorsSessionSearchResponse(await response.json());
}

async function fetchOperatorsUsageMonthly(input: {
  month?: string | null;
}): Promise<OperatorsUsageMonthlyResponse> {
  const url = new URL(`${getOperatorsHttpOrigin()}/api/v1/usage/monthly`);
  if (input.month) {
    url.searchParams.set("month", input.month);
  }

  const response = await fetch(url, {
    credentials: "include",
  });
  throwIfUnauthorized(response);
  await throwIfForbidden(response, "You do not have permission to view usage analytics.");
  if (!response.ok) {
    throw new Error(
      await readOperatorsAuthErrorMessage(response).catch(() => "Could not load usage analytics."),
    );
  }

  return (await response.json()) as OperatorsUsageMonthlyResponse;
}

export function operatorsActiveSessionsQueryOptions(input?: { enabled?: boolean }) {
  return queryOptions({
    queryKey: operatorsQueryKeys.activeSessions(),
    queryFn: async () => {
      const response = await fetchOperatorsSessions({
        limit: OPERATORS_ACTIVE_SESSIONS_PAGE_SIZE,
        status: "active",
      });
      return response.items;
    },
    enabled: input?.enabled ?? true,
    staleTime: 3_000,
    refetchInterval: 5_000,
    refetchOnWindowFocus: true,
  });
}

export function operatorsSidebarSessionsInfiniteQueryOptions(input: {
  enabled: boolean;
  email: string;
  group: "participating" | "other";
  poll?: boolean;
}) {
  return infiniteQueryOptions({
    queryKey: operatorsQueryKeys.sidebarSessions(input.email, input.group),
    initialPageParam: null as string | null,
    queryFn: ({ pageParam }) =>
      fetchOperatorsSessions({
        ...(pageParam ? { cursor: pageParam } : {}),
        status: "active",
        group: input.group,
        limit: OPERATORS_ACTIVE_SESSIONS_PAGE_SIZE,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: input.enabled && input.email.length > 0,
    staleTime: 3_000,
    refetchInterval: input.enabled && input.poll !== false ? 5_000 : false,
    refetchOnWindowFocus: true,
  });
}

export function operatorsArchivedSessionsInfiniteQueryOptions(input?: {
  enabled?: boolean;
  pageSize?: number;
}) {
  const pageSize = input?.pageSize ?? OPERATORS_ARCHIVED_SESSIONS_PAGE_SIZE;

  return infiniteQueryOptions({
    queryKey: operatorsQueryKeys.archivedSessions(pageSize),
    initialPageParam: null as string | null,
    queryFn: async ({ pageParam }) =>
      fetchOperatorsSessions({
        ...(pageParam ? { cursor: pageParam } : {}),
        limit: pageSize,
        status: "archived",
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: input?.enabled ?? true,
    staleTime: 30_000,
    refetchOnWindowFocus: false,
  });
}

export function operatorsSessionSearchQueryOptions(input: {
  enabled?: boolean;
  limit?: number;
  query: string;
  status?: "active" | "archived" | "all";
}) {
  const normalizedQuery = input.query.trim();
  const limit = input.limit ?? 50;
  const status = input.status ?? "all";
  return queryOptions({
    queryKey: operatorsQueryKeys.sessionSearch(normalizedQuery, status, limit),
    queryFn: async ({ signal }) =>
      fetchOperatorsSessionSearch({
        limit,
        query: normalizedQuery,
        signal,
        status,
      }),
    enabled: (input.enabled ?? true) && shouldUseOperatorsSessionSearchServerQuery(normalizedQuery),
    staleTime: 5_000,
    refetchOnWindowFocus: false,
  });
}

export function getCachedOperatorsSession(
  queryClient: QueryClient,
  sessionId: string,
): OperatorsSession | undefined {
  const activeSession = queryClient
    .getQueryData<OperatorsSession[]>(operatorsQueryKeys.activeSessions())
    ?.find((session) => session.id === sessionId);
  if (activeSession) {
    return activeSession;
  }

  const sidebarSession = queryClient
    .getQueriesData<InfiniteData<OperatorsSessionsResponse>>({
      queryKey: ["operators", "sessions", "sidebar"],
    })
    .flatMap(([, data]) => data?.pages ?? [])
    .flatMap((page) => page.items)
    .find((session) => session.id === sessionId);
  if (sidebarSession) return sidebarSession;

  const archivedSession = queryClient
    .getQueriesData<InfiniteData<OperatorsSessionsResponse>>({
      queryKey: ["operators", "sessions", "archived"],
    })
    .flatMap(([, data]) => data?.pages ?? [])
    .flatMap((page) => page.items)
    .find((session) => session.id === sessionId);

  return archivedSession;
}

export function operatorsSessionQueryOptions(input: {
  enabled?: boolean;
  initialData?: OperatorsSession | undefined;
  sessionId: string;
}) {
  return queryOptions({
    queryKey: operatorsQueryKeys.session(input.sessionId),
    queryFn: async () => fetchOperatorsSession(input.sessionId),
    enabled: input.enabled ?? true,
    initialData: input.initialData,
    staleTime: 3_000,
    refetchOnWindowFocus: true,
  });
}

export function operatorsUsageMonthlyQueryOptions(input: {
  enabled?: boolean;
  month?: string | null;
}) {
  const month = input.month ?? null;
  return queryOptions({
    queryKey: operatorsQueryKeys.usageMonthly(month),
    queryFn: async () => fetchOperatorsUsageMonthly({ month }),
    enabled: input.enabled ?? true,
    staleTime: 30_000,
    refetchInterval: 30_000,
    refetchOnWindowFocus: true,
  });
}
